class ApiError extends Error {
  statusCode;
  headers;
  body;
  cause;
  constructor(message, options = {}) {
    super(message);
    this.name = "ApiError";
    Object.setPrototypeOf(this, ApiError.prototype);
    this.statusCode = options.statusCode;
    this.headers = options.headers;
    this.body = options.body;
    this.cause = options.cause;
  }
}
const placeholders = (template, data) => {
  let resolved = typeof template === "function" ? template() : template;
  if (["string", "number"].indexOf(typeof resolved) === -1) {
    throw new Error("PlaceholdersJS: please provide a valid template");
  }
  const templateStr = String(resolved);
  if (!data) return templateStr;
  const result = templateStr.replace(/\{([^}]+)\}/g, function(match) {
    const key = match.slice(1, -1);
    const sub = key.split(".");
    if (sub.length > 1) {
      let temp = data;
      for (const item of sub) {
        if (temp === void 0 || temp === null || typeof temp !== "object") {
          return `{${key}}`;
        }
        if (!(item in temp)) {
          return `{${key}}`;
        }
        temp = temp[item];
      }
      return String(temp);
    }
    if (!(key in data)) {
      return `{${key}}`;
    }
    return String(data[key]);
  });
  return result;
};
class Serializer {
  #contentType;
  constructor(contentType) {
    this.#contentType = contentType?.toLowerCase();
  }
  serialize(val) {
    switch (this.#contentType) {
      case "application/json":
        return JSON.stringify(val);
      default:
        return val;
    }
  }
  deserialize(val) {
    if (this.#contentType?.includes("json")) {
      return JSON.parse(val);
    }
    return val;
  }
  deserializeHttpResponse(response) {
    try {
      const contentType = response.headers.get("content-type");
      if (contentType?.toLowerCase().includes("json")) {
        return response.json();
      }
      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  }
}
const strings = {
  trimEnd: (str, val) => {
    if (typeof str !== "string") {
      throw new Error("Parameter 'str' is not of expected type: string");
    }
    if (typeof val !== "string") {
      throw new Error("Parameter 'val' is not of expected type: string");
    }
    if (str.endsWith(val)) {
      return str.slice(0, val.length * -1);
    }
    return str;
  },
  trimStart: (str, val) => {
    if (typeof str !== "string") {
      throw new Error("Parameter 'str' is not of expected type: string");
    }
    if (typeof val !== "string") {
      throw new Error("Parameter 'val' is not of expected type: string");
    }
    if (str.startsWith(val)) {
      return str.slice(val.length);
    }
    return str;
  },
  trim: (str, val) => {
    return strings.trimStart(strings.trimEnd(str, val), val);
  },
  ensureIsString: (val) => {
    if (typeof val !== "string") {
      throw new Error("Parameter is not of expected type: string");
    }
    return true;
  },
  isOneOf: (str, expected) => {
    if (!expected.includes(str)) {
      throw new Error(`Unexpected value: ${str}. Expected one of: ${expected.join(",")}`);
    }
  }
};
class Request {
  #method = "GET";
  #headers = {};
  #body;
  method(method) {
    strings.ensureIsString(method);
    strings.isOneOf(method.toUpperCase(), ["GET", "POST", "PUT", "DELETE", "PATCH"]);
    this.#method = method;
    return this;
  }
  authorization(token, scheme = "Bearer") {
    if (token !== null && token !== void 0 && strings.ensureIsString(token)) {
      return this.addHeader("Authorization", `${scheme} ${token}`);
    }
    return this.removeHeader("Authorization");
  }
  contentType(contentType) {
    return this.addHeader("Content-Type", contentType);
  }
  body(body) {
    this.#body = body;
    return this;
  }
  addHeader(name, val) {
    this.#headers[name] = val;
    return this;
  }
  removeHeader(name = "Authorization") {
    if (Object.hasOwn(this.#headers, name)) {
      delete this.#headers[name];
    }
    return this;
  }
  build() {
    return {
      method: this.#method,
      headers: this.#headers,
      body: this.#body
    };
  }
}
const PROBLEM_JSON_CONTENT_TYPE = "application/problem+json";
function isProblemDetails(obj) {
  if (typeof obj !== "object" || obj === null) return false;
  const o = obj;
  return typeof o.status === "number" || typeof o.title === "string" || typeof o.type === "string" || typeof o.detail === "string" || typeof o.instance === "string";
}
class ResponseHandler {
  #serializer = new Serializer();
  constructor() {
    this.validate = this.validate.bind(this);
    this.deserialize = this.deserialize.bind(this);
    this.parseProblemJson = this.parseProblemJson.bind(this);
  }
  validate(response) {
    try {
      if (this.isSuccess(response)) {
        return Promise.resolve(response);
      }
      const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
      if (contentType.includes(PROBLEM_JSON_CONTENT_TYPE)) {
        return Promise.resolve(response);
      }
      return Promise.reject(
        new ApiError(`Request failed with Status Code: ${response.status}`, {
          statusCode: response.status,
          headers: response.headers
        })
      );
    } catch (err) {
      return Promise.reject(
        err instanceof ApiError ? err : new ApiError(String(err?.message ?? "Validation failed"), {
          cause: err
        })
      );
    }
  }
  deserialize(response) {
    const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
    const isProblem = contentType.includes(PROBLEM_JSON_CONTENT_TYPE);
    return this.#serializer.deserializeHttpResponse(response).then((body) => ({ body, isProblem })).catch(
      (err) => Promise.reject(
        new ApiError("Invalid JSON in response", {
          cause: err,
          statusCode: response.status,
          headers: response.headers
        })
      )
    );
  }
  parseProblemJson(result) {
    try {
      const { body, isProblem } = result;
      if (isProblem && isProblemDetails(body)) {
        const problem = body;
        const message = problem.detail ?? problem.title ?? `Request failed with Status Code: ${problem.status ?? "unknown"}`;
        return Promise.reject(
          new ApiError(message, {
            statusCode: typeof problem.status === "number" ? problem.status : void 0,
            body: problem
          })
        );
      }
      return Promise.resolve(body);
    } catch (err) {
      return Promise.reject(
        err instanceof ApiError ? err : new ApiError(String(err?.message ?? "Parse failed"), {
          cause: err
        })
      );
    }
  }
  isSuccess(response) {
    return response.status >= 200 && response.status < 400;
  }
}
class RequestHandler {
  #config;
  constructor(config) {
    this.#config = config;
  }
  GET(url, params, query, options) {
    return this.handleRequest("GET", url, params, query, null, options);
  }
  POST(url, params, query, body, options) {
    return this.handleRequest("POST", url, params, query, body, options);
  }
  PUT(url, params, query, body, options) {
    return this.handleRequest("PUT", url, params, query, body, options);
  }
  DELETE(url, params, query, _body, options) {
    return this.handleRequest("DELETE", url, params, query, null, options);
  }
  PATCH(url, params, query, body, options) {
    return this.handleRequest("PATCH", url, params, query, body, options);
  }
  handleRequest(method, url, params, query, body, options) {
    return new Promise((resolve, reject) => {
      this.#config.onRequireToken().then((token) => {
        const contentType = options?.contentType ?? this.#config.contentType;
        const request = new Request().method(method).authorization(token).contentType(contentType).body(this.serialize(body, contentType)).build();
        const endpoint = new URL(placeholders(url, params));
        for (const [key, value] of Object.entries(query)) {
          endpoint.searchParams.append(key, value);
        }
        const handler = new ResponseHandler();
        fetch(endpoint.href, {
          method: request.method,
          headers: request.headers,
          body: request.body
        }).then(handler.validate).then(handler.deserialize).then(handler.parseProblemJson).then(resolve).catch((err) => {
          reject(
            err instanceof ApiError ? err : new ApiError("Request failed", { cause: err })
          );
        });
      }).catch((err) => {
        reject(
          err instanceof ApiError ? err : new ApiError("Request failed", { cause: err })
        );
      });
    });
  }
  serialize(body, contentType) {
    if (body) {
      const serializer = new Serializer(contentType);
      return serializer.serialize(body);
    }
    return body;
  }
}
class BaseEndpoint {
  #baseUrl;
  #requestHandler;
  constructor(resource, config) {
    const trimmed = strings.trim(resource, "/");
    this.#baseUrl = placeholders("{config.baseUrl}/{resource}", {
      resource: trimmed,
      config
    });
    this.#requestHandler = new RequestHandler(config);
  }
  resourceUrl(id) {
    return placeholders("{baseUrl}/{id}", {
      baseUrl: this.#baseUrl,
      id: String(id)
    });
  }
  executeUrl() {
    return placeholders("{baseUrl}/rpc", { baseUrl: this.#baseUrl });
  }
  appendUrl(url, params = {}) {
    return placeholders("{baseUrl}{url}", {
      baseUrl: this.#baseUrl,
      url,
      ...params
    });
  }
  get(query = {}, options = {}) {
    return this.#requestHandler.GET(this.#baseUrl, {}, query, options);
  }
  fetch(id) {
    return this.#requestHandler.GET(
      this.resourceUrl(id),
      { id: String(id) },
      {},
      {}
    );
  }
  create(model) {
    return this.#requestHandler.POST(this.#baseUrl, {}, {}, model, {});
  }
  update(id, model) {
    return this.#requestHandler.PUT(
      this.resourceUrl(id),
      { id: String(id) },
      {},
      model,
      {}
    );
  }
  delete(id) {
    return this.#requestHandler.DELETE(
      this.resourceUrl(id),
      { id: String(id) },
      {},
      {},
      {}
    );
  }
  execute(method, params = {}) {
    const body = { ...params, op: method };
    return this.#requestHandler.POST(this.executeUrl(), {}, {}, body, {});
  }
  query(_mql) {
  }
  httpGET({
    url,
    params = {},
    query = {},
    options = {}
  }) {
    return this.#requestHandler.GET(url, params, query, options);
  }
  httpPOST({
    url,
    params = {},
    query = {},
    body,
    options = {}
  }) {
    return this.#requestHandler.POST(url, params, query, body, options);
  }
  httpPUT({
    url,
    params = {},
    query = {},
    body,
    options = {}
  }) {
    return this.#requestHandler.PUT(url, params, query, body, options);
  }
  httpDELETE({
    url,
    params = {},
    query = {},
    options = {}
  }) {
    return this.#requestHandler.DELETE(url, params, query, {}, options);
  }
  httpPATCH({
    url,
    params = {},
    query = {},
    body,
    options = {}
  }) {
    return this.#requestHandler.PATCH(url, params, query, body, options);
  }
}
const ACCESS_TOKEN_KEY = "livefreeid.idsrv.token";
const LiveFreeTokenLoader = () => {
  if (typeof localStorage === "undefined") {
    return Promise.resolve(null);
  }
  const raw = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!raw) {
    return Promise.resolve(null);
  }
  try {
    const token = JSON.parse(raw);
    return Promise.resolve(token?.accessToken ?? null);
  } catch {
    return Promise.resolve(null);
  }
};
class Config {
  #baseUrl = "";
  #contentType = "application/json";
  #onRequireToken;
  constructor(baseUrl, defaultContentType) {
    if (baseUrl) {
      this.baseUrl = baseUrl;
    }
    if (defaultContentType) {
      this.contentType = defaultContentType;
    }
  }
  get baseUrl() {
    return this.#baseUrl;
  }
  set baseUrl(val) {
    strings.ensureIsString(val);
    this.#baseUrl = strings.trimEnd(val, "/");
  }
  get contentType() {
    return this.#contentType;
  }
  set contentType(val) {
    strings.ensureIsString(val);
    this.#contentType = val.toLowerCase();
  }
  get onRequireToken() {
    return this.#onRequireToken ?? LiveFreeTokenLoader;
  }
  set onRequireToken(val) {
    this.#onRequireToken = val;
  }
}
class BaseSDK {
  #config;
  constructor(baseUrl, defaultContentType = "application/json") {
    this.#config = new Config(baseUrl, defaultContentType);
  }
  set onRequireToken(handler) {
    this.#config.onRequireToken = handler;
  }
  get config() {
    return this.#config;
  }
}
class RestClient extends BaseEndpoint {
  #config;
  constructor(baseUrl, resource, defaultContentType = "application/json") {
    const config = new Config(baseUrl, defaultContentType);
    super(resource, config);
    this.#config = config;
  }
  set onRequireToken(handler) {
    this.#config.onRequireToken = handler;
  }
  get config() {
    return this.#config;
  }
}
export {
  ApiError,
  BaseEndpoint,
  BaseSDK,
  Config,
  LiveFreeTokenLoader,
  RestClient
};
//# sourceMappingURL=index.js.map
